import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineClock, HiOutlineUsers, HiOutlineMapPin, HiOutlineTag, HiOutlineLockClosed, HiOutlineCheckBadge } from 'react-icons/hi2';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useAuth } from '../../contexts/AuthContext';
import './ExperiencesPage.css';

// Experience Card Component
const ExperienceCard = ({ experience, isAuthenticated, isApproved, affiliateTier }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewStats, setReviewStats] = useState({ totalNumberOfReviews: 0, averageRating: 0 });

  useEffect(() => {
    const fetchReviewStats = async () => {
      try {
        const reviewStatsRef = doc(db, 'experienceReviews', experience.id);
        const reviewStatsDoc = await getDoc(reviewStatsRef);
        if (reviewStatsDoc.exists()) {
          const data = reviewStatsDoc.data();
          setReviewStats({
            totalNumberOfReviews: data.totalNumberOfReviews || 0,
            averageRating: data.averageRating ? Number(data.averageRating.toFixed(1)) : 0
          });
        }
      } catch (error) {
        console.error('Error fetching review stats:', error);
      }
    };
    fetchReviewStats();
  }, [experience.id]);

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (experience.images && experience.images.length > 1) {
      setCurrentImageIndex((prev) => prev === experience.images.length - 1 ? 0 : prev + 1);
    }
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (experience.images && experience.images.length > 1) {
      setCurrentImageIndex((prev) => prev === 0 ? experience.images.length - 1 : prev - 1);
    }
  };

  const getRegularPrice = () => {
    if (!experience.options || experience.options.length === 0) {
      return { price: experience.pricePerPerson || 0, isPrivate: false };
    }
    const groupPrices = experience.options
      .filter(opt => opt.type === 'Group Experience' && opt.pricePerPerson)
      .map(opt => parseFloat(opt.pricePerPerson));
    const privatePrices = experience.options
      .filter(opt => opt.type === 'Private Experience' && opt.privateMinPrice)
      .map(opt => parseFloat(opt.privateMinPrice));
    
    const minGroupPrice = groupPrices.length > 0 ? Math.min(...groupPrices) : Infinity;
    const minPrivatePrice = privatePrices.length > 0 ? Math.min(...privatePrices) : Infinity;
    
    if (minGroupPrice <= minPrivatePrice && minGroupPrice !== Infinity) {
      return { price: minGroupPrice, isPrivate: false };
    } else if (minPrivatePrice !== Infinity) {
      return { price: minPrivatePrice, isPrivate: true };
    }
    return { price: 0, isPrivate: false };
  };

  const getAffiliatePricing = () => {
    if (!experience.options || experience.options.length === 0) return null;
    const tier = affiliateTier || 1;
    let lowestAffiliatePrice = Infinity;
    let hasAffiliatePricing = false;

    experience.options.forEach(option => {
      if (option.affiliatePricing?.enabled) {
        hasAffiliatePricing = true;
        // Use display price (with margin) first, fall back to legacy tier price for backward compat
        let tierPrice = option.affiliatePricing[`tier${tier}DisplayPrice`];
        if (tierPrice === undefined || tierPrice === null) {
          tierPrice = option.affiliatePricing[`tier${tier}Price`];
        }
        // For private experiences, also check min display price
        if (option.type === 'Private Experience') {
          let minPrice = option.affiliatePricing[`tier${tier}MinDisplayPrice`];
          if (minPrice === undefined || minPrice === null) {
            minPrice = option.affiliatePricing[`tier${tier}MinPrice`];
          }
          if (minPrice !== undefined && minPrice !== null) {
            if (minPrice === 0) { lowestAffiliatePrice = 0; }
            else if (minPrice < lowestAffiliatePrice) { lowestAffiliatePrice = minPrice; }
          }
        }
        if (tierPrice !== undefined && tierPrice !== null) {
          if (tierPrice === 0) {
            lowestAffiliatePrice = 0;
          } else if (tierPrice < lowestAffiliatePrice) {
            lowestAffiliatePrice = tierPrice;
          }
        }
      }
    });

    if (!hasAffiliatePricing) return null;
    return lowestAffiliatePrice === Infinity ? null : lowestAffiliatePrice;
  };

  const formatDuration = () => {
    if (!experience.options || experience.options.length === 0) return '';
    const durations = experience.options.map(opt => {
      const h = opt.duration || 0;
      const m = opt.durationMinutes || 0;
      return h * 60 + m;
    }).filter(t => t > 0);
    
    if (durations.length === 0) return '';
    const minDuration = Math.min(...durations);
    const maxDuration = Math.max(...durations);
    
    const formatMinutes = (total) => {
      const h = Math.floor(total / 60);
      const m = total % 60;
      if (h > 0 && m > 0) return `${h}h ${m}m`;
      if (h > 0) return `${h}h`;
      return `${m}m`;
    };
    
    if (minDuration === maxDuration) return formatMinutes(minDuration);
    return `${formatMinutes(minDuration)}-${formatMinutes(maxDuration)}`;
  };

  const getMaxGuests = () => {
    if (!experience.options || experience.options.length === 0) return 10;
    const maxValues = experience.options.map(opt => opt.maxGuests).filter(Boolean);
    return maxValues.length > 0 ? Math.max(...maxValues) : 10;
  };

  const priceInfo = getRegularPrice();
  const affiliatePrice = getAffiliatePricing();
  const hasAffiliatePrice = affiliatePrice !== null;
  const discountPercent = hasAffiliatePrice && priceInfo.price > 0 
    ? Math.round(((priceInfo.price - affiliatePrice) / priceInfo.price) * 100) 
    : 0;

  return (
    <Link to={`/experience/${experience.id}`} className="experience-card">
      <div className="experience-card-image-container">
        <div className="experience-badge">
          <HiOutlineCheckBadge />
          Experience
        </div>
        <div className="experience-category-badge">{experience.category}</div>
        
        {hasAffiliatePrice && isApproved && discountPercent > 0 && (
          <div className="experience-discount-badge">
            <HiOutlineTag />
            {affiliatePrice === 0 ? 'FREE' : `${discountPercent}% OFF`}
          </div>
        )}
        
        {experience.images && experience.images.length > 0 ? (
          <img 
            src={experience.images[currentImageIndex]} 
            alt={experience.name} 
            className="experience-image" 
          />
        ) : (
          <div className="experience-image-placeholder">No Image</div>
        )}
        
        {experience.images && experience.images.length > 1 && (
          <>
            <button className="nav-button prev" onClick={prevImage}>‹</button>
            <button className="nav-button next" onClick={nextImage}>›</button>
            <div className="image-dots">
              {experience.images.map((_, index) => (
                <span 
                  key={index} 
                  className={`dot ${index === currentImageIndex ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCurrentImageIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>
      
      <div className="experience-info">
        <div className="experience-header">
          <h3 className="experience-title">{experience.name}</h3>
          {reviewStats.totalNumberOfReviews > 0 ? (
            <div className="experience-rating">
              <span className="star">★</span>
              <span className="rating-score">{reviewStats.averageRating}</span>
              <span className="review-count">({reviewStats.totalNumberOfReviews})</span>
            </div>
          ) : (
            <span className="experience-new-badge">New</span>
          )}
        </div>
        
        <p className="experience-description">{experience.shortDescription}</p>
        
        <div className="experience-details">
          {formatDuration() && (
            <span className="experience-card-detail-item">
              <HiOutlineClock />
              {formatDuration()}
            </span>
          )}
          <span className="experience-card-detail-item">
            <HiOutlineUsers />
            Up to {getMaxGuests()} guests
          </span>
        </div>
        
        <div className="experience-location">
          <HiOutlineMapPin />
          <span>{experience.cityRegion || experience.location}</span>
        </div>
        
        <div className="experience-price">
          {hasAffiliatePrice && isApproved ? (
            <>
              <span className="price-label">from</span>
              <span className="original-price">€{priceInfo.price.toLocaleString()}</span>
              <span className="affiliate-price-tag">
                {affiliatePrice === 0 ? 'FREE' : `€${affiliatePrice.toLocaleString()}`}
              </span>
            </>
          ) : hasAffiliatePrice && isAuthenticated && !isApproved ? (
            <span className="locked-price">
              <HiOutlineLockClosed />
              Affiliate pricing available
            </span>
          ) : (
            <>
              <span className="price-label">from</span>
              <span className="price">€{priceInfo.price.toLocaleString()}</span>
              <span className="price-period">{priceInfo.isPrivate ? '/group' : '/person'}</span>
            </>
          )}
        </div>
      </div>
    </Link>
  );
};

const ExperiencesPage = () => {
  const { isAuthenticated, isApproved, affiliateTier } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      setLoading(true);
      const experiencesCollection = collection(db, 'experiences');
      const q = query(experiencesCollection, where('status', '==', 'active'));
      const experiencesSnapshot = await getDocs(q);
      
      const experiencesList = await Promise.all(
        experiencesSnapshot.docs.map(async (docSnap) => {
          const experience = { id: docSnap.id, ...docSnap.data() };
          
          const optionsDocRef = doc(db, 'experienceOptions', experience.id);
          const optionsDoc = await getDoc(optionsDocRef);
          
          if (optionsDoc.exists()) {
            experience.options = optionsDoc.data().options || [];
          } else {
            experience.options = [];
          }
          
          return experience;
        })
      );
      
      // Filter out experiences without options
      const experiencesWithOptions = experiencesList.filter(
        exp => exp.options && exp.options.length > 0
      );
      
      setExperiences(experiencesWithOptions);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching experiences:', error);
      setError('Failed to load experiences. Please try again.');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="experiences-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="experiences-page">
      <div className="experiences-hero">
        <h1>Discover Experiences</h1>
        <p>
          {isAuthenticated 
            ? isApproved 
              ? `Enjoy Tier ${affiliateTier || 1} affiliate pricing on all experiences`
              : 'Complete your application to unlock affiliate pricing'
            : 'Unique activities hosted by local experts'}
        </p>
      </div>

      <div className="experiences-alert">
        {error && (
          <div className="alert-banner error">
            <span className="alert-icon">⚠</span>
            {error}
          </div>
        )}

        {!isAuthenticated && !error && (
          <div className="alert-banner info">
            <span className="alert-icon">ℹ</span>
            <span><strong>Want better prices?</strong> <Link to="/signup">Sign up as an affiliate</Link> to unlock exclusive discounts.</span>
          </div>
        )}

        {isAuthenticated && !isApproved && !error && (
          <div className="alert-banner warning">
            <span className="alert-icon">⏳</span>
            <span><strong>Application pending?</strong> Once approved, you'll see your personalized affiliate prices.</span>
          </div>
        )}
      </div>

      <div className="experiences-grid">
        {experiences.length > 0 ? (
          experiences.map((experience) => (
            <ExperienceCard 
              key={experience.id} 
              experience={experience}
              isAuthenticated={isAuthenticated}
              isApproved={isApproved}
              affiliateTier={affiliateTier}
            />
          ))
        ) : (
          <div className="no-results">No experiences available at the moment</div>
        )}
      </div>
    </div>
  );
};

export default ExperiencesPage;
